'use client'

import { getTranslation } from 'labels/lib/getTranslation/getTranslation'
import type {
	LabelsTranslationsService,
	StringTranslationFunction,
} from 'labels/types'
import { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import type { Labels, LabelsNamespaces } from 'types/labels'
import { env } from 'utils/envManager/envManager'

interface UseClientLabelsProps {
	countryISO: string
	languageISO: string
	locale: string
	keys?: string[]
	namespaces?: LabelsNamespaces[]
	suspense?: boolean
	loadingTimeout?: number
}

export function useClientLabels({
	keys,
	namespaces,
	countryISO,
	languageISO,
	locale,
	suspense = true,
	loadingTimeout = 2000,
}: UseClientLabelsProps): {
	labels: Labels | undefined
	t: StringTranslationFunction
	isLoading: boolean | undefined
} {
	const doFetchKeys = keys && keys.length !== 0
	const doFetchNamespaces = namespaces && namespaces.length !== 0
	const url = `${env.NEXT_PUBLIC_API_DOMAIN}/ws/24h/labels/v2/labels`

	const { data: dataKeys, isLoading: isKeysLoading } =
		useSWRImmutable<LabelsTranslationsService | null>(
			[keys],
			() => {
				const searchParams = new URLSearchParams()
				searchParams.append('countryIso', countryISO.toUpperCase())
				searchParams.append('languageIso', languageISO)

				if (keys?.length) {
					const sortedKeys = [...new Set(keys)].sort((a: string, b: string) =>
						a.localeCompare(b)
					)
					searchParams.append('keys', sortedKeys.join(','))
				}

				searchParams.sort()

				const urlKeys = new URL(url)
				urlKeys.search = searchParams.toString()

				return doFetchKeys
					? fetch(urlKeys, {
							next: { revalidate: 0 },
							signal: AbortSignal.timeout(loadingTimeout),
						}).then((res) => res.json())
					: null
			},
			{ suspense, loadingTimeout }
		)

	const { data: dataNamespace, isLoading: isNamespaceLoading } =
		useSWRImmutable<LabelsTranslationsService | null>(
			[namespaces],
			() => {
				const searchParams = new URLSearchParams()
				searchParams.append('countryIso', countryISO.toUpperCase())
				searchParams.append('languageIso', languageISO)

				if (namespaces?.length) {
					const sortedNamespaces = [...new Set(namespaces)].sort(
						(a: LabelsNamespaces, b: LabelsNamespaces) => a.localeCompare(b)
					)
					searchParams.append('namespaces', sortedNamespaces.join(','))
				}

				searchParams.sort()

				const urlNamespace = new URL(url)
				urlNamespace.search = searchParams.toString()

				return doFetchNamespaces
					? fetch(urlNamespace, {
							next: { revalidate: 0 },
							signal: AbortSignal.timeout(loadingTimeout),
						}).then((res) => res.json())
					: null
			},
			{ suspense, loadingTimeout }
		)

	const data = useMemo(
		() => ({
			...(dataNamespace ? dataNamespace.translations : {}),
			...(dataKeys ? dataKeys.translations : {}),
			...(dataNamespace ? dataNamespace.plurals : {}),
			...(dataKeys ? dataKeys.plurals : {}),
		}),
		[dataKeys, dataNamespace]
	)

	return {
		isLoading:
			(doFetchKeys && isKeysLoading) ||
			(doFetchNamespaces && isNamespaceLoading),
		labels: data,
		t: (key, amount) =>
			getTranslation({
				amount,
				key,
				locale,
				translations: data,
			}),
	}
}
